import { Breadcrumbs, Center, SegmentedControl } from '@mantine/core';
import { IconCalendar, IconCalendarMonth, IconCalendarWeek, IconReload } from '@tabler/icons-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfigurationBox, InputField } from '../../../../components/Atoms';
import {
    DeployProjectSection,
    DeployTargetNotice,
    OperationSystemOptionList,
} from '../../../../components/Molecules';
import {
    BaremetalDeploymentSummaryCard,
    BaremetalPackageList,
} from '../../../../components/Organisms';
import { useActiveProject } from '../../../../hooks/useActiveProject';
import useBreadcrumbItems from '../../../../hooks/useBreadcrumbItems';
import usePageHeading from '../../../../hooks/usePageHeading';
import { useBareMetalComputeDeploymentPage } from './hooks/useBareMetalComputeDeploymentPage';

export default function BareMetalComputeDeploymentPage() {
    const { t } = useTranslation('bareMetalDeployment');
    const headingRef = usePageHeading(t('page.title'));
    const deployReasonId = useId();
    const canDeploy = useActiveProject().can('baremetal', 'manage');
    const breadcrumbItems = useBreadcrumbItems([
        { title: t('breadcrumb.product'), href: '#' },
        { title: t('breadcrumb.bareMetal'), href: '/bare-metal-compute' },
        {
            title: t('breadcrumb.deployment'),
            href: '/bare-metal-compute/deployment',
        },
    ]);

    const {
        hostname,
        hostnameError,
        isAvailableNodesPending,
        isDeployDisabled,
        isDeployLoading,
        isLaunchOptionsPending,
        osOptions,
        packages,
        paymentPeriod,
        selectedOs,
        selectedOsKind,
        selectedOsLabel,
        selectedOsInstallMinutes,
        selectedPackage,
        selectedVersionSlug,
        handleDeploy,
        handleGenerateHostname,
        handleHostnameChange,
        handleHostnamePaste,
        handleSelectOs,
        handleSelectVersion,
        setPaymentPeriod,
        setSelectedPackageSlug,
        setSelectedVersionSlug,
    } = useBareMetalComputeDeploymentPage();

    return (
        <div className="w-full min-h-full flex flex-col justify-start items-center gap-8">
            <div className="w-full flex flex-col justify-start items-start">
                <Breadcrumbs separatorMargin={'xs'} className="mb-6">
                    {breadcrumbItems}
                </Breadcrumbs>
                <div className="w-full flex flex-row justify-between">
                    <div className="flex flex-col justify-start items-start gap-1">
                        <h1 ref={headingRef} tabIndex={-1} className="text-3xl font-bold">
                            {t('page.title')}
                        </h1>
                        <p className="w-auto max-w-135 text-base text-fg/68">
                            {t('page.description')}
                        </p>
                    </div>
                </div>
            </div>
            <div className="w-full h-auto grid grid-cols-1 2xl:grid-cols-[2.5fr_1fr] items-start gap-4">
                <div className="w-full h-auto grid grid-cols-1 justify-self-center items-center gap-4">
                    <DeployProjectSection product="baremetal" />
                    <ConfigurationBox
                        label={t('steps.pickOs')}
                        leftDescription={t('steps.pickOsDescription')}
                    >
                        <OperationSystemOptionList
                            label={t('steps.pickOs')}
                            options={osOptions}
                            selectedOsSlug={selectedOs?.slug}
                            onSelectOs={handleSelectOs}
                            selectedVersionSlug={selectedVersionSlug}
                            onSelectVersion={handleSelectVersion}
                            versionPlaceholder={t('os.selectVersion')}
                            isLoading={isLaunchOptionsPending}
                        />
                    </ConfigurationBox>
                    <ConfigurationBox
                        label={t('steps.paymentPeriod')}
                        leftDescription={t('steps.paymentPeriodDescription')}
                    >
                        <div className="w-full">
                            <SegmentedControl
                                aria-label={t('steps.paymentPeriod')}
                                fullWidth
                                size="sm"
                                radius={'md'}
                                value={paymentPeriod}
                                onChange={(value) =>
                                    setPaymentPeriod(value as 'weekly' | 'monthly' | 'yearly')
                                }
                                styles={{
                                    root: {
                                        width: '100%',
                                        backgroundColor: 'var(--surface-2)',
                                        border: '0.5px solid var(--line)',
                                    },
                                }}
                                withItemsBorders={false}
                                data={[
                                    {
                                        value: 'weekly',
                                        label: (
                                            <Center style={{ gap: 6 }}>
                                                <IconCalendarWeek size={16} />
                                                <span style={{ fontWeight: 400 }}>
                                                    {t('paymentPeriod.weekly')}
                                                </span>
                                            </Center>
                                        ),
                                    },
                                    {
                                        value: 'monthly',
                                        label: (
                                            <Center style={{ gap: 6 }}>
                                                <IconCalendarMonth size={16} />
                                                <span style={{ fontWeight: 400 }}>
                                                    {t('paymentPeriod.monthly')}
                                                </span>
                                            </Center>
                                        ),
                                    },
                                    {
                                        value: 'yearly',
                                        label: (
                                            <Center style={{ gap: 6 }}>
                                                <IconCalendar size={16} />
                                                <span style={{ fontWeight: 400 }}>
                                                    {t('paymentPeriod.yearly')}
                                                </span>
                                            </Center>
                                        ),
                                    },
                                ]}
                            />
                        </div>
                    </ConfigurationBox>
                    <ConfigurationBox
                        label={t('steps.pickPackage')}
                        leftDescription={t('steps.pickPackageDescription')}
                        footer={
                            <span className="text-yellow-400/80 light:text-yellow-800">
                                {t('steps.pickPackageFooter')}
                            </span>
                        }
                    >
                        <div className="w-full">
                            <BaremetalPackageList
                                options={packages}
                                selectedSlug={selectedPackage?.slug}
                                onSelect={setSelectedPackageSlug}
                                paymentPeriod={paymentPeriod}
                                isLoading={isLaunchOptionsPending || isAvailableNodesPending}
                            />
                        </div>
                    </ConfigurationBox>
                    <ConfigurationBox
                        label={t('steps.hostname')}
                        leftDescription={t('steps.hostnameDescription')}
                    >
                        <div className="w-full flex flex-col justify-center items-center gap-4">
                            <InputField
                                size="md"
                                label={<h3 className="text-base">{t('hostname.label')}</h3>}
                                placeholder={t('hostname.placeholder')}
                                value={hostname}
                                pattern="[A-Za-z0-9-]+"
                                minLength={4}
                                maxLength={20}
                                onChange={(e) => handleHostnameChange(e.target.value || '')}
                                onPaste={handleHostnamePaste}
                                error={hostnameError}
                                rightIcon={
                                    <button
                                        type="button"
                                        onClick={handleGenerateHostname}
                                        aria-label={t('hostname.generateAriaLabel')}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: 0,
                                            border: 'none',
                                            background: 'transparent',
                                            color: 'var(--color-text-muted, currentColor)',
                                            cursor: 'pointer',
                                            opacity: 0.7,
                                            transition: 'opacity 0.15s ease',
                                        }}
                                        onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                                        onMouseLeave={(e) =>
                                            (e.currentTarget.style.opacity = '0.7')
                                        }
                                    >
                                        <IconReload size={20} />
                                    </button>
                                }
                                descriptionBottom={t('hostname.description')}
                            />
                        </div>
                    </ConfigurationBox>
                </div>
                <BaremetalDeploymentSummaryCard
                    hostname={hostname}
                    paymentPeriod={paymentPeriod}
                    selectedPackage={selectedPackage}
                    selectedOsLabel={selectedOsLabel}
                    selectedOsKind={selectedOsKind}
                    installMinutes={selectedOsInstallMinutes}
                    isDeployLoading={isDeployLoading}
                    isDeployDisabled={isDeployDisabled}
                    onDeploy={handleDeploy}
                    notice={<DeployTargetNotice product="baremetal" reasonId={deployReasonId} />}
                    deployBlockedReasonId={canDeploy ? undefined : deployReasonId}
                />
            </div>
        </div>
    );
}
